import React, { useEffect } from 'react';
import useAxios from 'axios-hooks';
import isEmpty from 'lodash.isempty';
import numeral from 'numeral';

import JournalStructure from '../../../models/journal-structure.json';
import MeasurementTypes from '../../../models/measurement-types.json';
import { API_URL } from '../../../config/constants';
import { short, time } from '../common/formatting/date';
import { fullname } from '../common/formatting/provider';
import { Button } from '../../../components';
import logo from '../../../../img/cc-logo-2022-color.svg';
import { useParams } from 'react-router-dom';

const EVISIT_FILTER = JSON.stringify({
	include: [
		{
			relation: 'patient',
			scope: {
				include: [
					{
						relation: 'insurances',
						scope: {
							limit: 1,
							order: ['id DESC'],
						},
					},
				],
			},
		},
		'provider',
		'patient_dependent',
		'provider_visit_type',
		'care_quest_response_groups',
		{
			relation: 'evisit_provider_transactions',
			scope: {
				limit: 5,
				order: 'update_dttm DESC',
				include: 'provider',
			},
		},
		{ 'evisit-referral': { 'closedBy-provider': 'provider_practice' } },
	],
});

const DischargeSummary = () => {
	const { evisitId } = useParams();
	const [{ data: evisit /* loading, error */ }] = useAxios({
		method: 'GET',
		url: `/evisits/${evisitId}`,
		params: {
			filter: EVISIT_FILTER,
		},
	});
	const [
		{ data: care_quest_responses /* loading: isLoadingCQRs, error */ },
		loadCQRs,
	] = useAxios(
		{
			method: 'GET',
			params: {
				filter: JSON.stringify({
					include: [
						{
							relation: 'care_quest_question',
							scope: {
								order: ['question_order'],
								include: 'care_quest_answers',
								scope: {
									order: ['answer_order'],
								},
							},
						},
					],
				}),
			},
		},
		{ manual: true }
	);
	const [{ data: addendumData /* loading: isLoadingAddendum, error */ }] =
		useAxios({
			method: 'GET',
			url: `/evisits/${evisitId}/evisit_addendum`,
			params: {
				filter: JSON.stringify({
					include: ['provider'],
				}),
			},
		});
	const [{ data: diagnoses }] = useAxios({
		method: 'GET',
		url: `/evisits/${evisitId}/evisit_diagnosis`,
		params: {
			filter: JSON.stringify({
				include: ['icd_code', 'procedure_code'],
			}),
		},
	});
	const [{ data: images }] = useAxios({
		method: 'GET',
		url: `/evisit_images/${evisitId}/files`,
	});
	const [{ data: providers }] = useAxios({
		method: 'GET',
		url: `/evisits/${evisitId}/chat-participants`,
	});
	const [{ data: chatArchive }] = useAxios({
		method: 'GET',
		url: `/evisits/${evisitId}/evisit_chats`,
		params: {
			filter: JSON.stringify({
				where: {
					and: [
						{ text: { neq: 'video_requested' } },
						{ text: { neq: 'video_started' } },
						{ text: { neq: 'video_ended' } },
						{ text: { neq: 'video_declined' } },
						{ text: { neq: 'photos_uploaded' } },
					],
				},
				order: ['sent_dttm ASC'],
			}),
		},
	});
	const [{ data: providerPractice }, loadProviderPractice] = useAxios(
		{
			method: 'GET',
			url: `/evisits/${evisitId}/chat-participants`,
		},
		{ manual: true }
	);

	useEffect(() => {
		if (
			evisit &&
			Array.isArray(evisit.care_quest_response_groups) &&
			evisit.care_quest_response_groups.length > 0
		) {
			const questResponse = evisit.care_quest_response_groups[0];
			loadCQRs({
				url: `/care_quest_response_groups/${questResponse.id}/care_quest_responses`,
			});
		}
		if (evisit && evisit.providerPracticeId) {
			loadProviderPractice({
				url: `/provider_practices/${evisit.providerPracticeId}`,
			});
		}
	}, [evisit]);

	if (!evisit) {
		return <div>Loading...</div>;
	}

	const handlePrint = () => {
		window.print();
	};

	const handleClose = () => {
		window.history.back();
	};

	const practice = providerPractice || {};
	const practiceLogo = `${API_URL}/provider_practice_logos/${practice.id}/download/${practice.logo_image_name}`;
	const { provider, patient } = evisit;
	let evisitReport;
	if (evisit.evisit_report) {
		let journalType, journalDetail;
		evisit.evisitReport = JSON.parse(evisit.evisit_report);
		if (evisit.evisitReport.healthjournal) {
			const healthJournal = evisit.evisitReport.healthjournal;
			healthJournal.forEach(function (journalObj) {
				journalType = JournalStructure.find(
					({ type }) => type === journalObj.journal_type
				);
				if (journalType) {
					journalObj.journal_type_label = journalType.label;
					journalDetail = journalType.options.find(
						({ type }) => type === journalObj.journal_detail
					);
					if (journalDetail) {
						journalObj.journal_detail_label = journalDetail.label;
					}
				}
			});
			evisit.evisitReport.healthjournal = healthJournal.filter(
				({ journal_type_label, resolved_dttm }) =>
					isEmpty(resolved_dttm) && !isEmpty(journal_type_label)
			);
		}
		if (evisit.evisitReport.vitals) {
			evisit.evisitReport.vitals.forEach(function (vitalObj) {
				var i;
				var name = vitalObj.name;
				var value = vitalObj.value;
				var measurementConfig = MeasurementTypes.find(
					({ value }) => value === name
				);
				var values;
				var preparedValue = [];
				if (measurementConfig) {
					values = value.split('|');
					for (i = 0; i < measurementConfig.fields.length; i++) {
						if (measurementConfig.fields[i].format) {
							preparedValue.push(
								numeral(values[i]).format(measurementConfig.fields[i].format)
							);
						} else {
							preparedValue.push(values[i]);
						}
						if (measurementConfig.fields[i].suffix) {
							preparedValue.push(measurementConfig.fields[i].suffix);
						}
					}

					vitalObj.label = measurementConfig.label;
				}
				vitalObj.preparedValue =
					preparedValue.length > 0 ? preparedValue.join(' ') : value;
			});
		}
	}

	// const insurance = evisit.get('insurance') ? evisit.get('insurance').toJSON() : {};
	const isDependentEvisit =
		Boolean(evisit.patientDependentId) && evisit.patient_dependent;

	if (providers?.cb && chatArchive) {
		chatArchive.forEach((message) => {
			// patient='first name', provider='lastname, firstname credentials'
			var messageSrc = message.participant_type;
			if (messageSrc === 'patient' && patient.id === message.participantId) {
				message.name = patient.name_first;
			} else if (messageSrc === 'provider') {
				const provider = providers.cb.find(
					({ id }) => id == message.participantId
				);
				if (provider) {
					message.name =
						provider.name_last +
						', ' +
						provider.name_first +
						' ' +
						(provider.credentials || '');
				} else {
					message.name = 'Provider';
				}
			} else {
				message.name = 'SYSTEM';
			}
		});
	}

	let selfAssessment = [];
	if (
		evisit.care_quest_response_groups &&
		evisit.care_quest_response_groups.length > 0
	) {
		if (Array.isArray(care_quest_responses)) {
			selfAssessment = care_quest_responses.reduce((responses, response) => {
				const existing = responses.find(
					(r) => r.care_quest_questionId === response.care_quest_questionId
				);
				if (existing) {
					if (response.care_quest_answerId) {
						const answer = response.care_quest_question.care_quest_answers.find(
							({ id }) => id === response.care_quest_answerId
						);
						if (answer) {
							existing.answerText = [
								existing.answerText,
								answer.answer_text,
							].join(', ');
						}
					}
				} else {
					let answerText;
					if (response.care_quest_answerId) {
						const answer = response.care_quest_question.care_quest_answers.find(
							({ id }) => id === response.care_quest_answerId
						);
						if (answer) {
							answerText = answer.answer_text;
						}
					} else if ('Date' === response.care_quest_question.question_type) {
						answerText = short(response.care_quest_answer_text);
					} else {
						answerText = response.care_quest_answer_text;
					}
					responses.push({
						questionText: response.care_quest_question.question_text,
						answerText,
						...response,
					});
				}
				return responses;
			}, []);
		}
	}

	let evisit_addendum;
	if (Array.isArray(addendumData)) {
		evisit_addendum = addendumData.map((addendum) => ({
			...addendum,
			providerName: fullname(addendum.provider),
			timeAdded: `${short(addendum.addendum_dttm)} ${time(
				addendum.addendum_dttm
			)}`,
		}));
	}

	return (
		<React.Fragment>
			<div className="print-buttons">
				<Button icon="print" className="mr-sm" onClick={handlePrint} />
				<Button icon="times" onClick={handleClose} />
			</div>
			<div id="print-content" className="print-content">
				<div className="cc-logo">
					<img src={logo} />
				</div>
				<div className="text-center">
					{practice && practice.logo_image_name && (
						<img
							src={practiceLogo}
							style={{ width: '200px', height: 'auto' }}
						/>
					)}
				</div>
				<div className="text-center">
					<strong>Visit #:</strong> {evisit.id} &nbsp; &nbsp;{' '}
					<strong>Visit Date:</strong> {short(evisit.start_dttm)}
				</div>
				<h2>Provider Information</h2>
				<div className="provider-info">
					<p>
						<strong>Provider: </strong>
						{provider.name_first} {provider.name_last}, {provider.credentials}
					</p>
					<p>
						<strong>Location Address: </strong> {practice.practice_name} -{' '}
						{practice.address1} {practice.address2} {practice.city}{' '}
						{practice.state} {practice.zip}
					</p>
				</div>
				<h2>Patient Details</h2>
				<div className="patient-details">
					<p>
						{isDependentEvisit ? (
							<React.Fragment>
								<strong>Patient Name:</strong>
								{evisit.patient_dependent.dependent_name_last},{' '}
								{evisit.patient_dependent.dependent_name}
								(dependent of {patient.name_first} {patient.name_last})
								&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
								<strong>Sex:</strong>{' '}
								{evisit.patient_dependent.dependent_gender}{' '}
								&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
								<strong>DOB:</strong>{' '}
								{short(evisit.patient_dependent.dependent_birth_dttm)}{' '}
								&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
							</React.Fragment>
						) : (
							<React.Fragment>
								<strong>Patient Name:</strong> {patient.name_last},{' '}
								{patient.name_first} {patient.name_middle}{' '}
								&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
								<strong>Sex:</strong> {patient.gender}{' '}
								&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
								<strong>DOB:</strong> {short(patient.birth_dttm)}{' '}
								&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
							</React.Fragment>
						)}
						<strong>Phone:</strong> {evisit.phone_cell}
					</p>
					<p>
						<strong>Address:</strong> {patient.address} {patient.city}{' '}
						{patient.state} {patient.zip}
					</p>
					{/* <p><strong>Insurance:</strong> {{#if insuranceId}}zzz{{else}}Self Pay{{/if}}</p> */}
					<p>
						<strong>Reason for Visit:</strong>{' '}
						{evisit.provider_visit_type.visit_type}{' '}
					</p>
					<p>
						<strong>Patient Comments:</strong> {evisit.comments}
					</p>
				</div>
				{selfAssessment.length > 0 && (
					<React.Fragment>
						<h2>Self Assessment</h2>
						<div>
							{selfAssessment.map(({ questionText, answerText }) => (
								<React.Fragment key={`${questionText}::${answerText}`}>
									<p>
										<strong>{questionText}</strong>
									</p>
									<ul>
										<li>
											{isEmpty(answerText) ? <em>NO RESPONSE</em> : answerText}
										</li>
									</ul>
								</React.Fragment>
							))}
						</div>
					</React.Fragment>
				)}
				<h2>Subjective</h2>
				<div>
					<p className="white-space-pre">
						{isEmpty(evisit.evisit_subjective) ? (
							<strong>No subjective made.</strong>
						) : (
							evisit.evisit_subjective
						)}
					</p>
				</div>
				<h2>Objective</h2>
				<div>
					<p className="white-space-pre">
						{isEmpty(evisit.evisit_objective) ? (
							<strong>No objective made.</strong>
						) : (
							evisit.evisit_objective
						)}
					</p>
				</div>
				<h2>Assessment</h2>
				<div className="assessment">
					<p className="white-space-pre">
						{isEmpty(evisit.evisit_assessment) ? (
							<strong>No assessment made.</strong>
						) : (
							evisit.evisit_assessment
						)}
					</p>
				</div>
				<h2>Plan/Patient Instructions</h2>
				<div className="plan-instructions">
					<p className="white-space-pre">
						{isEmpty(evisit.patient_instructions) ? (
							<strong>No plan or patient instructions created.</strong>
						) : (
							evisit.patient_instructions
						)}
					</p>
				</div>
				{Array.isArray(evisit_addendum) && evisit_addendum.length > 0 && (
					<React.Fragment>
						<h2>Addendum</h2>
						{evisit_addendum.map(
							({
								id,
								addendum_type,
								providerName,
								timeAdded,
								addendum_text,
							}) => (
								<div key={`${id}`}>
									<h4>
										{addendum_type}{' '}
										<small>
											{providerName} - {timeAdded}
										</small>
									</h4>
									<blockquote>
										<p>{addendum_text}</p>
									</blockquote>
								</div>
							)
						)}
					</React.Fragment>
				)}
				<h2>Diagnosis - ICD10/Procedures</h2>
				{Array.isArray(diagnoses) && diagnoses.length > 0 ? (
					<div className="evisit-diagnosis">
						{diagnoses.map(({ icd_code, procedure_code }) => (
							<p key={`${icd_code}::${procedure_code}`}>
								<strong>{icd_code.icd_code}</strong> -{' '}
								{icd_code.icd_code_description}
								<br />
								<strong>{procedure_code.procedure_code}</strong> -{' '}
								{procedure_code.procedure_code_description}
							</p>
						))}
					</div>
				) : (
					<p>No diagnoses found.</p>
				)}
				<h2>Patient Chat/Visit Images</h2>
				<div className="chat-images">
					<h4>Patient Chat</h4>
					{chatArchive && chatArchive.length > 0 ? (
						chatArchive.map((message) => (
							<p key={`${message.id}`}>
								{message.name} &middot; {short(message.sent_dttm)} at{' '}
								{time(message.sent_dttm)} &mdash; {message.text}
							</p>
						))
					) : (
						<p>
							<strong>No chat data found.</strong>
						</p>
					)}
					<h4>Visit Images</h4>
					{Array.isArray(images) && images.length > 0 ? (
						<div className="clearfix">
							{images.map((image) => (
								<div
									key={`${image.container}/${image.name}`}
									className="pull-left"
								>
									<img
										src={`${API_URL}/evisit_images/${image.container}/download/${image.name}`}
									/>
								</div>
							))}
						</div>
					) : (
						<p>
							<strong>No visit images found.</strong>
						</p>
					)}
				</div>
				{evisitReport && (
					<React.Fragment>
						<h2>Patient Stated Health Information</h2>
						<div className="patient-health-info">
							{Array.isArray(evisitReport.allergies) &&
								evisitReport.allergies.length > 0 && (
									<React.Fragment>
										<h4>Allergies</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Allergy</th>
													<th>Onset Date</th>
													<th>Reaction</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.allergies.map((allergy) => (
													<tr key={`allergy-${allergy.id}`}>
														<td>{allergy.name}</td>
														<td>{short(allergy.onset_dttm)}</td>
														<td>{allergy.reaction}</td>
													</tr>
												))}
											</tbody>
										</table>
									</React.Fragment>
								)}
							{Array.isArray(evisitReport.problems) &&
								evisitReport.problems.length > 0 && (
									<React.Fragment>
										<h4>Problems</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Problem</th>
													<th>Onset Date</th>
													<th>Severity</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.problems.map((problem) => (
													<tr key={`problem-${problem.id}`}>
														<td>{problem.provider_display_name}</td>
														<td>{short(problem.onset_dttm)}</td>
														<td>{problem.severity}</td>
													</tr>
												))}
											</tbody>
										</table>
									</React.Fragment>
								)}
							{Array.isArray(evisitReport.medications) &&
								evisitReport.medications.length > 0 && (
									<React.Fragment>
										<h4>Medications</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Medication</th>
													<th>Taking</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.medications.map(
													({ id, name, status }) => (
														<tr key={`medication-${id}`}>
															<td>{name}</td>
															<td>
																{status === 'taking' ? 'Active' : 'Historical'}
															</td>
														</tr>
													)
												)}
											</tbody>
										</table>
									</React.Fragment>
								)}
							{Array.isArray(evisitReport.vitals) &&
								evisitReport.vitals.length > 0 && (
									<React.Fragment>
										<h4>Vitals and Measurements</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Measurement</th>
													<th>Value</th>
													<th>Date Taken</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.vitals.map(
													({ id, label, preparedValue, vital_dttm }) => (
														<tr key={`vital-${id}`}>
															<td>{label}</td>
															<td>{preparedValue}</td>
															<td>{short(vital_dttm)}</td>
														</tr>
													)
												)}
											</tbody>
										</table>
									</React.Fragment>
								)}
							{Array.isArray(evisitReport.procedures) &&
								evisitReport.procedures.length > 0 && (
									<React.Fragment>
										<h4>Procedures</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Procedure</th>
													<th>Procedure Date</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.procedures.map(
													({ id, name, date_performed }) => (
														<tr key={`procedure-${id}`}>
															<td>{name}</td>
															<td>{short(date_performed)}</td>
														</tr>
													)
												)}
											</tbody>
										</table>
									</React.Fragment>
								)}
							{Array.isArray(evisitReport.immunizations) &&
								evisitReport.immunizations.length > 0 && (
									<React.Fragment>
										<h4>Immunizations</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Name</th>
													<th>Date Given</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.immunizations.map(
													({ id, name, date_given }) => (
														<tr key={`immunization-${id}`}>
															<td>{name}</td>
															<td>{short(date_given)}</td>
														</tr>
													)
												)}
											</tbody>
										</table>
									</React.Fragment>
								)}
							{Array.isArray(evisitReport.healthjournal) &&
								evisitReport.healthjournal.length > 0 && (
									<React.Fragment>
										<h4>Current Symptoms</h4>
										<table className="table table-striped">
											<thead>
												<tr>
													<th>Type</th>
													<th>Symptom</th>
													<th>Onset Date</th>
													<th>Additional Details</th>
												</tr>
											</thead>
											<tbody>
												{evisitReport.healthjournal.map(
													({
														id,
														journal_type_label,
														journal_detail_label,
														onset_dttm,
														additional_details,
													}) => (
														<tr key={`journalentry-${id}`}>
															<td>{journal_type_label}</td>
															<td>{journal_detail_label}</td>
															<td>{short(onset_dttm)}</td>
															<td>{additional_details}</td>
														</tr>
													)
												)}
											</tbody>
										</table>
									</React.Fragment>
								)}
						</div>
					</React.Fragment>
				)}
			</div>
		</React.Fragment>
	);
};

export default DischargeSummary;
